import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { post, setAccessToken, refreshAccessToken, registerRefreshedUserCallback, registerUnauthorizedCallback } from '../api/client';
import { queryClient } from '../lib/queryClient';

export interface AdminUser {
  id: string;
  username: string;
  fullName?: string;
  role: string;
  /**
   * 后端算好的管理员标记（role 为 'admin' 或历史值 '管理员' 时都为 true）。
   * 后台的准入判定一律读它，不要再自己比对 role 字符串。
   */
  isAdmin?: boolean;
}

interface Session {
  accessToken: string;
  expiresAt: number;
}

interface AuthState {
  user: AdminUser | null;
  session: Session | null;
  loading: boolean;
  _hydrate: () => Promise<void>;
  signIn: (identifier: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  refreshUser: (user: unknown) => void;
}

/**
 * 统一的「会话结束」清空动作：退出登录与 401 失效回调都走这一条路。
 *
 * 除令牌与 store 状态外还必须清空 react-query 缓存：用户、客户、审核、支付等
 * 查询结果都在内存里，同一台机器换管理员登录时，新管理员首屏会先渲染上一位
 * 管理员的数据，直到各自请求返回才被覆盖。
 */
function clearSession() {
  queryClient.clear();
  setAccessToken(null);
  useAuthStore.setState({ user: null, session: null, loading: false });
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      session: null,
      loading: true,

      _hydrate: async () => {
        // 启动时静默恢复会话：refreshToken 在 HttpOnly Cookie 里，浏览器自动带上。
        // 必须走 refreshAccessToken()（与 401 重放同一条单飞），不能在这里自己 fetch——
        // 同一条 refreshToken 发两次就会被服务端判成令牌复用并吊销整族，登录后刷新页面必掉线。
        try {
          const token = await refreshAccessToken();
          if (!token) {
            clearSession();
            return;
          }
          // refreshAccessToken 已经过 refreshUser 回调把 user 落进 store（非管理员会被它清空）
          const currentUser = get().user;
          if (!currentUser?.isAdmin) {
            clearSession();
            return;
          }
          set({
            session: { accessToken: token, expiresAt: Date.now() + 15 * 60 * 1000 },
            loading: false,
          });
        } catch {
          clearSession();
        }
      },

      signIn: async (identifier, password) => {
        set({ loading: true });
        try {
          const data = await post<{ accessToken: string; user: AdminUser }>('/auth/login', {
            identifier,
            password,
          });
          if (!data?.user || !data.user.isAdmin) {
            throw new Error('该账号不具备管理员权限');
          }
          setAccessToken(data.accessToken);
          set({
            user: data.user,
            session: {
              accessToken: data.accessToken,
              expiresAt: Date.now() + 15 * 60 * 1000,
            },
            loading: false,
          });
        } catch (error) {
          set({ loading: false });
          throw error;
        }
      },

      signOut: async () => {
        try {
          await post('/auth/logout');
        } catch {
          /* ignore */
        }
        clearSession();
      },

      refreshUser: (user) => {
        const typed = user as AdminUser | null;
        if (typed && typed.isAdmin) set({ user: typed });
        else set({ user: null, session: null });
      },
    }),
    {
      name: 'admin-auth',
      // 仅持久化 user 用于 UI 快速显示；真实认证以 HttpOnly refreshToken cookie 为准
      partialize: (state) => ({ user: state.user }),
    },
  ),
);

registerRefreshedUserCallback((user) => useAuthStore.getState().refreshUser(user));
registerUnauthorizedCallback(() => clearSession());